import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, from, switchMap, BehaviorSubject } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { ApiUrlService } from './api-url.service';
import { AuthService } from './auth.service';
import { SKIP_AUTH } from './auth.interceptor';

export interface Tenant {
  tenantId: string;
  name?: string;
  displayName?: string;
  defaultHostConfig?: {
    surchargeAmount?: string;
    withdrawalIncrementAmount?: string;
    maximumWithdrawalAmount?: string;
    minInterArrivalMs?: string;
    healthCheckEnabled?: boolean;
    healthCheckIntervalHours?: string;
    bankIdNumber?: string;
    reversalEnabled?: boolean;
    keyRefreshDelaySeconds?: string;
    sessionKeyExpiryHours?: string;
    terminalProcessingDelayMs?: string;
    visa2Options?: {
      ackResponseEnabled?: boolean;
      eotTimeoutMs?: number;
      maxRetries?: number;
      parity?: string;
      responseTimeoutMs?: number;
      stxLrcEnabled?: boolean;
      retryDelayMs?: number;
      retransmitCount?: number;
      enqEnabled?: boolean;
      commsTimer?: number;
    };
  };
  defaultCassetteConfig?: {
    cassette1?: {
      maxCapacity: number;
      reloadThreshold: number;
      reloadWarningThreshold?: number;
      reloadCriticalThreshold?: number;
      denomination: number;
      noteCount: number;
    };
    cassette2?: {
      maxCapacity: number;
      reloadThreshold: number;
      reloadWarningThreshold?: number;
      reloadCriticalThreshold?: number;
      denomination: number;
      noteCount: number;
    };
    cassette3?: {
      maxCapacity: number;
      reloadThreshold: number;
      reloadWarningThreshold?: number;
      reloadCriticalThreshold?: number;
      denomination: number;
      noteCount: number;
    };
    cassette4?: {
      maxCapacity: number;
      reloadThreshold: number;
      reloadWarningThreshold?: number;
      reloadCriticalThreshold?: number;
      denomination: number;
      noteCount: number;
    };
  };
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface AdminTenantApiResponse {
  status: string;
  data: {
    tenants: Array<Tenant>;
  };
}

export interface UserTenantApiResponse {
  success: boolean;
  data: {
    userId: string;
    tenants: Array<Tenant>;
    pagination: {
      count: number;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private httpOptions = {
    context: new HttpContext().set(SKIP_AUTH, true)
  };

  private tenants$ = new BehaviorSubject<Tenant[]>([]);
  private isLoaded = false;

  constructor(
    private http: HttpClient,
    private apiUrlService: ApiUrlService,
    private authService: AuthService
  ) {}

  private loadTenants(): Observable<Tenant[]> {
    if (this.isLoaded && this.tenants$.value.length > 0) {
      console.log('Returning cached tenants', this.tenants$.value);
      return this.tenants$.asObservable();
    }

    return from(this.authService.getUser()).pipe(
      switchMap(user => {
        if (!user) {
          throw new Error('User not authenticated');
        }

        const options = this.apiUrlService.isUsingLocalServer() ? this.httpOptions : {};
        const baseUrl = this.apiUrlService.buildApiUrl('admin');
        
        if (user.userType === 'admin') {
          return this.http.get<AdminTenantApiResponse>(`${baseUrl}/tenants?limit=100`, options).pipe(
            map(response => {
              if (response.data && response.data.tenants) {
                return response.data.tenants;
              }
              return [];
            })
          );
        } else {
          return this.http.get<UserTenantApiResponse>(`${baseUrl}/users/${user.email}/tenants`, options).pipe(
            map(response => {
              if (response.data && response.data.tenants) {
                return response.data.tenants;
              }
              return [];
            })
          );
        }
      }),
      tap(tenants => {
        if (tenants.length === 0) {
          this.isLoaded = true;
          void this.authService.logoutUserNotFound();
          return;
        }
        this.tenants$.next(tenants);
        this.isLoaded = true;
      }),
      catchError((error: unknown) => {
        console.error('Error fetching tenants:', error);
        this.tenants$.next([]);
        throw error;
      })
    );
  }

  getTenantsIds(): Observable<string[]> {
    return this.loadTenants().pipe(
      map(tenants => tenants.map(tenant => tenant.tenantId))
    );
  }

  getTenantById(tenantId: string): Observable<Tenant | undefined> {
    return this.loadTenants().pipe(
      map(tenants => tenants.find(tenant => tenant.tenantId === tenantId))
    );
  }
}